<template>
  <div>
    <div class="notice">{{ $t('register.trial_text') }}</div>

    <v-form @submit.prevent="register">

      <div class="input-field">
        <div>
          <v-text-field outlined flat
            v-model.trim.lazy="email"
            @input="resetError()"
            @blur="$v.email.$touch();checkEmail()"
            :error-messages="emailErrors">
            <template v-slot:label>
              {{ $t('register.email') }}
            </template>
          </v-text-field>
        </div>
      </div>

      <div class="input-field">
        <div>
          <v-text-field outlined flat
            v-model.trim.lazy="username"
            @input="resetError()"
            @blur="$v.username.$touch();checkUsername()"
            :error-messages="usernameErrors">
            <template v-slot:label>
              {{ $t('register.username') }}
            </template>
          </v-text-field>
        </div>
      </div>

      <div class="input-fields">
        <div class="input-field">
          <div>
            <v-text-field outlined flat
              v-model.lazy="lastname"
              @input="resetError()"
              @blur="$v.lastname.$touch()"
              :error-messages="lastnameErrors">
              <template v-slot:label>
                {{ $t('register.lastname') }}
              </template>
            </v-text-field>
          </div>
        </div>

        <div class="input-field">
          <div>
            <v-text-field outlined flat
              v-model.lazy="firstname"
              @input="resetError()"
              @blur="$v.firstname.$touch()"
              :error-messages="firstnameErrors">
              <template v-slot:label>
                {{ $t('register.firstname') }}
              </template>
            </v-text-field>
          </div>
        </div>
      </div>

      <div class="input-fields">
        <div class="input-field">
          <div>
            <v-text-field outlined flat
              v-model.lazy="password"
              type="password"
              @input="resetError()"
              @blur="$v.password.$touch()"
              :error-messages="passwordErrors">
              <template v-slot:label>
                {{ $t('register.password') }}
              </template>
            </v-text-field>
          </div>
        </div>

        <div class="input-field">
          <div>
            <v-text-field outlined flat
              v-model.lazy="password_re"
              type="password"
              @input="resetError()"
              @blur="$v.password_re.$touch()"
              :error-messages="passwordReErrors">
              <template v-slot:label>
                {{ $t('register.password_re') }}
              </template>
            </v-text-field>
          </div>
        </div>
      </div>

      <div class="description" v-html="$t('register.agree_description')"></div>

      <div v-if="error">
        <span class="required">
          <v-icon color="error" small>mdi-alert-circle-outline</v-icon>
          {{ $t('alert.try_again') }}
        </span>
      </div>

      <div class="actions">
        <v-btn block depressed dark type="submit"
          color="pms205">
          {{ $t('actions.next') }}
        </v-btn>
      </div>
    </v-form>

  </div>
</template>

<script>
  import { mapGetters } from 'vuex'
  import { required, email, minLength, maxLength, sameAs } from 'vuelidate/lib/validators'

  export default {

    props: {
      busy: {}
    },

    data() {
      return {
        username: "",
        firstname: "",
        lastname: "",
        email: "",
        password: "",
        password_re: "",

        requiredRules: [
          v => !!v || this.$t('alert.required')
        ],

        error: false,
      }
    },

    // async created() {
    //   this.email = !!this.$store.getters['welcome/email'] ?
    //     this.$store.getters['welcome/email'] : ""
    // },

    computed: {
      ...mapGetters({
        available_username: 'auth/available_username',
        available_email: 'auth/available_email',
      }),

      usernameErrors() {
        const errors = []
        if (!this.$v.username.$dirty) return errors
        !this.$v.username.required && errors.push(this.$t('register.required'))
        !this.$v.username.isUnique && errors.push(this.$t('register.username_not_available'))
        !this.$v.username.goodUsername && errors.push(this.$t('register.username_not_valid'))
        
        return errors
      },

      firstnameErrors() {
        const errors = []
        if (!this.$v.firstname.$dirty) return errors
        !this.$v.firstname.required && errors.push(this.$t('register.required'))
        return errors
      },

      lastnameErrors() {
        const errors = []
        if (!this.$v.lastname.$dirty) return errors
        !this.$v.lastname.required && errors.push(this.$t('register.required'))
        return errors
      },

      emailErrors() {
        const errors = []
        if (!this.$v.email.$dirty) return errors
        !this.$v.email.required && errors.push(this.$t('alert.required'))
        !this.$v.email.checkEmail && errors.push(this.$t('alert.email_not_valid'))
        !this.$v.email.isUnique && errors.push(this.$t('alert.email_not_available'))
        return errors
      },

      passwordErrors() {
        const errors = []
        if (!this.$v.password.$dirty) return errors
        !this.$v.password.required && errors.push(this.$t('register.required'))
        !this.$v.password.goodPassword && errors.push(this.$t('register.password_not_valid'))
        return errors
      },

      passwordReErrors() {
        const errors = []
        if (!this.$v.password_re.$dirty) return errors
        !this.$v.password_re.required && errors.push(this.$t('register.required'))
        !this.$v.password_re.sameAs && errors.push(this.$t('register.password_not_same'))
        return errors
      },

    },

    validations: {
      username: {
        required,
        isUnique() {
          return this.$v.username.$dirty &&
            this.$v.username.required &&
            this.available_username
        },
        goodUsername(username) {
          return /^[.A-Za-z0-9]+$/.test(username) &&
            username.length >=6 && username.length <31
        },
      },
      firstname: { required },
      lastname: { required },
      email: { 
        required, 
        checkEmail: email,
        isUnique() {
          return this.$v.email.$dirty &&
            this.$v.email.required &&
            this.available_email
        }
      },
      password: {
        required,
        goodPassword(password) {
          return /[a-z]/.test(password) &&
          /[0-9]/.test(password) &&
          password.length >= 8 && password.length < 31
        }
      },
      password_re: {
        required,
        sameAs: sameAs('password')
      },
    },

    methods: {
      updateBusy(state) {
        // console.log(state)
        this.$emit('update:busy', state)
      },

      resetError() {
        if (this.error) {
          this.error = false
        }
      },

      async checkUsername() {
        if (this.username.length>0) {
          await this.$store.dispatch('auth/checkUsername', {
            username: this.username
          })
        }
      },

      async checkEmail() {
        if (this.email.length>0) {
          await this.$store.dispatch('auth/checkEmail', {
            email: this.email
          })
        }
      },

      async register() {
        await this.$v.$touch()
        if (!this.$v.$anyError) {
          this.updateBusy(true)
          let language = this.$store.getters['auth/language'] || 'ko'
          try {
            await this.$store.dispatch('auth/register', {
              username: this.username,
              firstname: this.firstname,
              lastname: this.lastname,
              email: this.email,
              password: this.password,
              language: language,
            })
          } catch(e) {
            this.error = true
          }
          finally {
            this.updateBusy(false)
            if (!this.error) {
              await this.startTrial()
            }
          }
        }
      },

      async startTrial() {
        let prod_id = null
        try {
          await this.$store.dispatch('welcome/getProducts')
        } catch(e) {
          this.error = true
        } finally {
          let all_prods = this.$store.getters['welcome/products']
          prod_id = (all_prods.find(v => v.kind == 1)).prod_id
          try {
            await this.$store.dispatch('payment/startTrial', {
              product: prod_id,
            })
          } catch(e) {
            this.error = true
          } finally {
            if (!this.error) {
              let payment = this.$store.getters['payment/payment']
              this.$analytics.logEvent('purchase', { 
                transactions_id: payment.pay_id,
                value: payment.amount,
                currency: payment.unit,
                product: payment.product,
                name: payment.product_name,
                kind: payment.kind,
                status: payment.status,
              })
              this.$analytics.logEvent('sign_up')
              this.$router.push({name:'signupform', params: {code: 'plans'}})
            }
          }
        }
      },
    },
  }
</script>

<style scoped>
  .actions {
    width: 100%;
    display: flex;
    padding-top: 20px;
    justify-content: flex-end;
    align-items: center;
  }

  .actions {
    flex-direction: column;
  }

  .notice {
    display: flex;
    justify-content: center;
    align-items: center;
    margin-bottom: 20px;
    font-size: 16px;
  }

  .input-fields {
    display: grid;
    grid-template-columns: 48% 48%;
    grid-column-gap: 4%;
  }
</style>


// WEBPACK FOOTER //
// src/components/shared_components/form/Register.vue